import React, { ReactNode } from "react";
import { View, StyleSheet } from "react-native";
import { SessionSummary as SessionSummaryData } from "@/store/types";
import { colors, layout, typography } from "@/constants";
import Accordion from "./base/Accordion";
import Text from "./base/Text";
import Pill from "./base/Pill";
import Icon, { IconName } from "./base/Icon";
import Spinner from "./base/Spinner";

/**
 * Props for the internal SummarySection component
 */
interface SummarySectionProps {
  /** Section label */
  label: string;
  /** Section content text (used when children is not provided) */
  content?: string;
  /** Custom content to render (takes precedence over content) */
  children?: ReactNode;
  /** Icon to display before the label */
  icon: IconName;
}

/**
 * Internal component for displaying a separator between sections.
 */
function Separator() {
  return <View style={styles.separator} />;
}

/**
 * Internal component for displaying a labeled section with content.
 * Used to avoid repetition in the SessionSummary component.
 * Supports both text content and custom children (e.g., Pills).
 */
function SummarySection({
  label,
  content,
  children,
  icon
}: SummarySectionProps) {
  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <Icon name={icon} size="small" color="secondary" />
        <Text variant="semiBold" size="small" style={styles.sectionLabel}>
          {label}
        </Text>
      </View>
      {children ? (
        children
      ) : (
        <Text size="medium" style={styles.sectionContent}>
          {content}
        </Text>
      )}
    </View>
  );
}

/**
 * Displays session summary (DAP + Themes) in a collapsible accordion.
 * Shows Data, Assessment, Plan sections with separators and Themes as pills.
 *
 * Owns its own visibility: returns null when there are no insights yet, or
 * when generation settled ("current") with no Discussion content — the
 * minimum bar for a meaningful summary (short sessions can land here).
 *
 * @param props - The component props
 * @returns A collapsible session summary, or null when nothing to show
 */
export default function SessionSummary({
  insights
}: {
  insights: SessionSummaryData | null;
}) {
  if (!insights) return null;

  const isBeingGenerated =
    insights.status === "queued" || insights.status === "processing";
  const hasDiscussion = !!insights.dap.data;

  if (!isBeingGenerated && !hasDiscussion) return null;

  const sections = [
    insights.themes && insights.themes.length > 0
      ? {
          label: "Themes",
          icon: "label" as const,
          children: (
            <View style={styles.themesContainer}>
              {insights.themes.map((theme, index) => (
                <Pill
                  key={index}
                  label={theme}
                  accessibilityLabel={`Theme: ${theme}`}
                />
              ))}
            </View>
          )
        }
      : null,
    insights.dap.data
      ? {
          label: "Discussion",
          content: insights.dap.data,
          icon: "notes" as const
        }
      : null,
    insights.dap.assessment
      ? {
          label: "Insights",
          content: insights.dap.assessment,
          icon: "insights" as const
        }
      : null,
    insights.dap.plan
      ? {
          label: "Action Plan",
          content: insights.dap.plan,
          icon: "flag" as const
        }
      : null
  ].filter((section) => section !== null);

  return (
    <Accordion
      headerChildren={
        <>
          <Icon
            name="format_list_bulleted"
            variant="medium"
            style={styles.headerAccent}
          />
          <Text variant="medium" style={styles.headerAccent}>
            Session Summary
          </Text>
          {isBeingGenerated && (
            <Spinner
              size="small"
              color={colors.onSurfaceVariant}
              style={styles.spinner}
            />
          )}
        </>
      }
      contentChildren={
        <>
          {sections.map((section, index) => (
            <React.Fragment key={index}>
              <SummarySection
                label={section.label}
                content={section.content}
                icon={section.icon}
              >
                {section.children}
              </SummarySection>
              {index < sections.length - 1 && <Separator />}
            </React.Fragment>
          ))}
        </>
      }
      defaultExpanded={false}
      disabled={isBeingGenerated}
      accessibilityLabel="Session Summary"
    />
  );
}

const styles = StyleSheet.create({
  section: {
    gap: layout.space.sm
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: layout.space.xs
  },
  sectionLabel: {
    color: colors.onSurfaceVariant,
    fontSize: typography.fontSizes.small
  },
  sectionContent: {
    color: colors.onSurface,
    lineHeight: layout.size.md
  },
  separator: {
    height: 1,
    backgroundColor: colors.outline
  },
  themesContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: layout.space.sm
  },
  themePill: {
    paddingHorizontal: layout.space.md,
    paddingVertical: layout.space.sm,
    borderRadius: layout.borderRadius.round,
    borderWidth: layout.borderWidth.thin,
    borderColor: colors.outline,
    backgroundColor: colors.surfaceContainerHighest
  },
  themeText: {
    color: colors.onSurface
  },
  spinner: {
    marginLeft: layout.space.xs
  },
  headerAccent: {
    color: colors.onAiContainer
  }
});
